import {
  Component,
  Dropdown,
  Image as _Image,
  Video as _Video,
  RichText,
  Section,
  Switch as _Switch,
  Textfield,
  Screen,
  DxaSurvey,
  Slider,
  Color,
} from '../formFields'
import { BUTTON_VARIANTS, HEADING_LEVELS } from './_common'

export * from './web'

const LINK_TOOLTIP =
  'Screen to navigate to on click. Redirect is performed after triggering events and sending webhook requests.'

const title = Textfield({ name: 'title', label: 'Title' })
const subtitle = RichText({ name: 'subtitle', label: 'Subtitle' })
const buttonVariant = Dropdown({
  name: 'buttonVariant',
  label: 'Variant',
  options: BUTTON_VARIANTS,
  filter: null,
})

export const CustomerHubVideo = Component('CustomerHubVideo', {
  description: 'Displays a video player with a video/image in the background.',
  thumbnail: 'https://s3.amazonaws.com/adobedemohub/milo/Luma/customer-hub-video.png',
  category: 'Customer Hub',
  fields: [
    Dropdown({
      name: 'variant',
      label: 'Variant',
      tooltip: 'Please refer to the template documentation',
      options: [
        { label: 'Variant 1', value: 'variant1' },
        { label: 'Variant 2', value: 'variant2' },
      ],
      hidden: null,
      filter: null,
    }),
    RichText({
      name: 'tagline',
      label: 'Tagline',
      tooltip: 'Customer Tagline, additional styles may be applied depending on the variant',
    }),
    Section(
      'Background',
      _Video({ name: 'backgroundVideoUrl', label: 'Background Video URL' }),
      _Image({ name: 'backgroundImageUrl', label: 'Background Image' })
    ),
    Section(
      'Main Video',
      _Video({ name: 'videoUrl', label: 'Video URL' }),
      _Image({ name: 'posterUrl', label: 'Video Poster' })
    ),
    Section(
      'Logo',
      _Switch({ name: 'includeLogo', label: 'Include Customer/Adobe Logo', defaultChecked: true }),
      Textfield({ name: 'logoHeight', label: 'Customer Logo Height' })
    ),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user', variant: 'variant1' },
  },
})

export const CustomerHubVisionDemo = Component('CustomerHubVisionDemo', {
  description: 'Displays an iframe with video or XD preview.',
  thumbnail: 'https://s3.amazonaws.com/adobedemohub/milo/Luma/customer-hub-vision-demo.png',
  category: 'Customer Hub',
  fields: [
    Section(
      'Main',
      _Video({ name: 'videoUrl', label: 'Video URL', tooltip: 'Link to a video/mp4 file' }),
      Textfield({ name: 'xdUrl', label: 'XD URL', tooltip: 'Link to an XD prototype' })
    ),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user' },
  },
})

export const CustomerHubSharedVision = Component('CustomerHubSharedVision', {
  description: 'Displays customer Shared Vision',
  thumbnail: 'https://s3.amazonaws.com/adobedemohub/milo/Luma/customer-hub-shared-vision.png',
  category: 'Customer Hub',
  fields: [
    Section(
      'Left Card',
      _Image({ name: 'leftImage', label: 'Image' }),
      RichText({ name: 'leftQuote', label: 'Quote' }),
      RichText({ name: 'leftDetails', label: 'Details', tooltip: 'Customer executive name and title' }),
      Textfield({ name: 'leftLogoHeight', label: 'Logo Height' })
    ),
    Section(
      'Right Card',
      _Image({ name: 'rightImage', label: 'Image' }),
      RichText({ name: 'rightQuote', label: 'Quote' }),
      RichText({ name: 'rightDetails', label: 'Details', tooltip: 'Adobe CEO name and title' }),
      Textfield({ name: 'rightLogoHeight', label: 'Logo Height' })
    ),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user' },
  },
})

export const CustomerHubLogo = Component('CustomerHubLogo', {
  description: 'Displays Customer and Adobe Logo (Monochrome) with optional Scroll Up button.',
  thumbnail: 'https://s3.amazonaws.com/adobedemohub/milo/Luma/customer-hub-logo.png',
  category: 'Customer Hub',
  fields: [
    _Switch({ name: 'showScrollUpButton', label: 'Show Scroll Up Button', defaultChecked: false }),
    Textfield({ name: 'logoHeight', label: 'Customer Logo Height' }),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user' },
  },
})

export const CustomerHubSuccessStory = Component('CustomerHubSuccessStory', {
  description: 'Displays a customer Success Story card.',
  thumbnail: 'https://s3.amazonaws.com/adobedemohub/milo/Luma/customer-hub-success-story.png',
  category: 'Customer Hub',
  fields: [
    Section(
      'Images',
      _Image({ name: 'image', label: 'Image' }),
      _Image({ name: 'logo', label: 'Logo' }),
      Slider({
        name: 'logoScale',
        label: 'Logo scale',
        displayAs: 'percentage',
        min: 0.5,
        max: 1.5,
        step: 0.005,
      })
    ),
    Dropdown({ name: 'titleLevel', label: 'Title size', options: HEADING_LEVELS, filter: null }),
    Screen({
      name: 'link',
      label: 'Link to',
      filter: null,
      allowExternal: true,
      allowProduct: true,
      tooltip: LINK_TOOLTIP,
    }),
    Section(
      'Button Appearance',
      Textfield({ name: 'buttonLabel', label: 'Label' }),
      _Switch({ name: 'buttonIsQuiet', label: 'Quiet (no border)', defaultChecked: false })
    ),
    Section('Texts', title, subtitle, RichText({ name: 'details', label: 'Details' })),
  ],
  defaultContent: {
    content: { title: 'Lorem Ipsum', titleLevel: 2, selectable: false },
  },
})

export const CustomerHubDxaSurvey = Component('CustomerHubDxaSurvey', {
  label: 'DXA Survey',
  description: 'Displays a DXA Survey. Requires user with @adobe.com email to link DXA project.',
  category: 'Customer Hub',
  fields: [
    DxaSurvey({
      name: 'dxaSurveyId',
      label: 'DXA Survey',
      tooltip: 'List of DXA projects. If you need to manage or create surveys, visit https://dxa.corp.adobe.com',
    }),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: {},
    styles: { height: '2100px' },
  },
})

export const Icon = Component('Icon', {
  description: 'Displays svg icon.',
  category: 'Customer Hub',
  fields: [_Image({ name: 'svg', label: 'SVG' })],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user' },
  },
})

export const CustomerHubStrategicPillar = Component('CustomerHubStrategicPillar', {
  description: 'Displays card with an svg icon.',
  thumbnail: 'https://s3.amazonaws.com/adobedemohub/milo/Luma/customer-hub-strategic-pillar.png',
  category: 'Customer Hub',
  fields: [
    _Image({ name: 'svg', label: 'SVG' }),
    Dropdown({ name: 'titleLevel', label: 'Title size', options: HEADING_LEVELS, filter: null }),
    Section('Texts', title, subtitle),
    Section('Button appearance', Textfield({ name: 'buttonLabel', label: 'Label' }), buttonVariant),
    Section(
      'Icon appearance',
      Color({ name: 'iconFill', label: 'Fill color' }),
      Color({ name: 'iconStrokeColor', label: 'Stroke color' }),
      Textfield({ name: 'iconStrokeWidth', label: 'Stroke width' })
    ),
  ],
  exposedStyles: [
    { label: 'Width', name: 'width', placeholder: 'eg. auto or 640px' },
    { label: 'Height', name: 'height', placeholder: 'eg. auto or 360px' },
  ],
  defaultContent: {
    content: { mode: 'user' },
  },
})

export const DEFAULT_COMPONENT = { meta: { component: 'Title' }, content: { title: 'Title', level: 1 } }
